import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Reoverlay } from "reoverlay";
import ConsentDialog from "./dialogs/ConsentDialog";
import SettingsDialog from "./dialogs/SettingsDialog";

const COOKIE_NAME = "de.myjobboard.perbitInsightMyJobboard.MetaPixel";

interface CookieConsentContextType {
  consentGiven: boolean | null;
  resetConsent: () => void;
  openSettings: () => void;
  settingsDialogOpen: boolean;
  consentDialogOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType>({
  consentGiven: null,
  resetConsent: () => {},
  openSettings: () => {},
  settingsDialogOpen: false,
  consentDialogOpen: false,
});

export const useCookieConsent = () => useContext(CookieConsentContext);

export const CookieConsentProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);

  useEffect(() => {
    const consent = Cookies.get(COOKIE_NAME);
    if (consent === undefined && !consentDialogOpen) {
      setConsentDialogOpen(true);
      Reoverlay.showModal(ConsentDialog, {
        onConsent: (consent: boolean) => {
          Cookies.set(COOKIE_NAME, consent.toString(), { expires: 365 });
          setConsentGiven(consent);
          setConsentDialogOpen(false);
          Reoverlay.hideModal();
        }
      });
    } else {
      setConsentGiven(consent === 'true');
    }
  }, []);

  const resetConsent = () => {
    setSettingsDialogOpen(false);
    Reoverlay.hideModal();
    Cookies.remove(COOKIE_NAME);
    setConsentGiven(null);
    setConsentDialogOpen(true);
    Reoverlay.showModal(ConsentDialog, {
      onConsent: (consent: boolean) => {
        Cookies.set(COOKIE_NAME, consent.toString(), { expires: 365 });
        setConsentGiven(consent);
        setConsentDialogOpen(false);
        Reoverlay.hideModal();
      }
    });
  };

  const openSettings = () => {
    setSettingsDialogOpen(true);
    Reoverlay.showModal(SettingsDialog, {
      onClose: () => {
        setSettingsDialogOpen(false);
        Reoverlay.hideModal();
      },
      onChangeConsent: resetConsent
    });
  };

  return (
    <CookieConsentContext.Provider value={{ consentGiven, resetConsent, openSettings, settingsDialogOpen, consentDialogOpen }}>
      {children}
    </CookieConsentContext.Provider>
  );
};
